import { AppyError } from '../../../../../Appy';
import { GroupResource } from '../../../../../GroupsStore/Types';
import { MissingLinkError } from '../../../../../LinkyLinky';
import { MulticheckStore } from '../../../../../Multicheck/Store';
import {
  FormOutValues,
  FormTemplate,
  MissingFieldError,
} from '../../../../../Resource/FormTemplates/Types';
import { Link, Resource } from '../../../../../Resource/Types';
import {
  InvalidFormValues,
  MissingTemplateError,
} from '../../../Participants/BulkActions/AllocateLicenseToParticipants/Types';
import {
  LicenseAllocationsFormResource,
  LicenseResource,
} from '../../../Participants/Table/LicenseAllocations/Types';

// Groups-specific types for bulk license allocation (simple allocation only, no release)
export interface GroupBulkLicenseSubmittedPayload {
  allocatedLicenses: ReadonlyArray<LicenseResource>;
  userIds: ReadonlyArray<number>;
}

export type GroupBulkLicenseSubmittedResource = Resource<GroupBulkLicenseSubmittedPayload>;

// Alias for use in state machine
export type SubmittedResource = GroupBulkLicenseSubmittedResource;

export interface Waiting {
  kind: 'waiting';
}

export interface Loading {
  kind: 'loading';
  link: Link;
  multicheckStore: MulticheckStore<GroupResource>;
}

export type LoadError =
  | AppyError
  | MissingTemplateError
  | MissingLinkError
  | MissingFieldError
  | InvalidFormValues;

export interface LoadingError {
  kind: 'loading-error';
  multicheckStore: MulticheckStore<GroupResource>;
  error: LoadError;
}

export type ReadyResource = LicenseAllocationsFormResource;

export interface Ready {
  kind: 'ready';
  resource: ReadyResource;
  template: FormTemplate;
  multicheckStore: MulticheckStore<GroupResource>;
}

export interface Submitting {
  kind: 'submitting';
  resource: ReadyResource;
  template: FormTemplate;
  values: FormOutValues;
  multicheckStore: MulticheckStore<GroupResource>;
}

export type SubmitError = AppyError | MissingLinkError;

export interface SubmittingError {
  kind: 'submitting-error';
  resource: ReadyResource;
  template: FormTemplate;
  error: SubmitError;
}

export interface Submitted {
  kind: 'submitted';
  resource: SubmittedResource;
  template: FormTemplate;
  multicheckStore: MulticheckStore<GroupResource>;
}

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const loading = (link: Link, multicheckStore: MulticheckStore<GroupResource>): Loading => ({
  kind: 'loading',
  link,
  multicheckStore,
});

export const loadingError = (
  multicheckStore: MulticheckStore<GroupResource>,
  error: LoadError,
): LoadingError => ({
  kind: 'loading-error',
  multicheckStore,
  error,
});

export const ready = (
  resource: ReadyResource,
  template: FormTemplate,
  multicheckStore: MulticheckStore<GroupResource>,
): Ready => ({
  kind: 'ready',
  resource,
  template,
  multicheckStore,
});

export const submitting = (
  resource: ReadyResource,
  template: FormTemplate,
  values: FormOutValues,
  multicheckStore: MulticheckStore<GroupResource>,
): Submitting => ({
  kind: 'submitting',
  resource,
  template,
  values,
  multicheckStore,
});

export const submittingError = (
  resource: ReadyResource,
  template: FormTemplate,
  error: SubmitError,
): SubmittingError => ({
  kind: 'submitting-error',
  resource,
  template,
  error,
});

export const submitted = (
  resource: SubmittedResource,
  template: FormTemplate,
  multicheckStore: MulticheckStore<GroupResource>,
): Submitted => ({
  kind: 'submitted',
  resource,
  template,
  multicheckStore,
});

export type State =
  | Waiting
  | Loading
  | LoadingError
  | Ready
  | Submitting
  | SubmittingError
  | Submitted;

export interface SubmissionSuccess {
  kind: 'submission-success';
  resource: SubmittedResource;
}

export interface ValidationErrors {
  kind: 'validation-errors';
  resource: ReadyResource;
}
export type SubmissionResponse = SubmissionSuccess | ValidationErrors;
